import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { withAccount } from '@/utils/withAccount';

const StyledBadge = styled(Badge)(({ theme, color, enableRipple }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: color,
    color: color,
    width: '15px',
    height: '15px',
    borderRadius: '50%',
    fontSize: '1rem',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '1px solid currentColor',
      content: '""',
      ...(enableRipple
        ? {
            animation: 'ripple 1.2s infinite ease-in-out',
          }
        : {}),
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const decodeAndFormatPath = (path) => {
  return decodeURIComponent(path).replace(/%20/g, ' ');
};

const capitalizeFirstLetter = (string) => {
  // If the string matches known static paths, format normally
  const staticPathNames = [
    'buildings',
    'devices',
    'gateways',
    'calendar',
    'operations',
    'portfolioanalysis',
  ]; // Add all your static paths here
  if (staticPathNames.includes(string.toLowerCase())) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  // Otherwise, leave camel case names intact (don't split by capital letters)
  return string;
};

const CustomBreadcrumbs = () => {
  const location = useLocation();
  const { accountId } = useParams();
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x && x !== 'home')
    .slice(1); // Remove the accountId from the breadcrumbs

  const navigate = useNavigate();
  const { t } = useTranslation('glossary');

  const handleNavigateToHome = () => {
    navigate(withAccount(accountId, '/'));
  };

  return (
    <Breadcrumbs
      separator={
        <NavigateNextIcon
          fontSize="small"
          sx={{
            color: (theme) =>
              theme.palette.mode === 'light'
                ? 'hsl(0, 0%, 20%)'
                : 'hsl(0, 0%, 85%)',
          }}
        />
      }
      aria-label="breadcrumb"
      noWrap
      sx={{ flexGrow: 1 }}
      color="text.primary"
    >
      <Tooltip
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              color="#44b700"
              enableRipple={false}
              sx={{ mx: 1 }}
            />
            <Typography
              variant="label"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)',
              }}
            >
              1.3
            </Typography>
          </Box>
        }
        placement="bottom"
      >
        <Link
          underline="none"
          onClick={handleNavigateToHome}
          sx={{
            fontFamily: 'neuropolitical',
            ':hover': {
              cursor: 'pointer',
              '.M': { color: 'hsl(63, 100%, 45%)' },
            },
          }}
        >
          <Box>
            <Typography
              variant="label"
              className="Re"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)',
                fontWeight: 'medium',
              }}
            >
              Re
            </Typography>
            <Typography
              variant="label"
              className="M"
              sx={{
                color: 'hsl(63, 100%, 40%)',
                fontWeight: 'medium',
              }}
            >
              M
            </Typography>
            <Typography
              variant="label"
              className="oni"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)',
                fontWeight: 'medium',
              }}
            >
              oni
            </Typography>
          </Box>
        </Link>
      </Tooltip>
      {pathnames.map((value, index) => {
        const last = index === pathnames.length - 1;
        const to = withAccount(
          accountId,
          `/${pathnames.slice(0, index + 1).join('/')}`,
        );
        const label =
          t(capitalizeFirstLetter(decodeAndFormatPath(value))) ||
          capitalizeFirstLetter(decodeAndFormatPath(value));

        if (label === 'energyxplorer') {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'left',
              }}
            >
              <Typography
                variant="label"
                sx={{
                  fontFamily: 'JetBrains Mono NL',
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'hsl(0, 0%, 20%)'
                      : 'hsl(0, 0%, 85%)',
                  fontWeight: 'bold',
                }}
              >
                Energy
              </Typography>
              <Typography
                variant="label"
                sx={{
                  fontFamily: 'JetBrains Mono NL',
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'hsl(0, 0%, 20%)'
                      : 'hsl(0, 0%, 85%)',
                  fontWeight: 'bold',
                }}
              >
                X
              </Typography>
              <Typography
                variant="label"
                sx={{
                  fontFamily: 'JetBrains Mono NL',
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'hsl(0, 0%, 20%)'
                      : 'hsl(0, 0%, 85%)',
                  fontWeight: 'bold',
                }}
              >
                plorer
              </Typography>
            </Box>
          );
        }

        return last ? (
          <Typography
            sx={{
              fontWeight: 'bold',
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? 'hsl(0, 0%, 20%)'
                  : 'hsl(0, 0%, 85%)',
            }}
            key={to}
          >
            {label}
          </Typography>
        ) : (
          <Link
            noWrap
            sx={{
              flexGrow: 1,
              ':hover': {
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)',
              },
            }}
            color="text.secondary"
            component={RouterLink}
            underline="none"
            to={to}
            state={location.state?.from?.state || location.state || {}}
            key={to}
          >
            {label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default CustomBreadcrumbs;
